// File: frontend/src/services/categoryService.js

//============================================================================
// CATEGORY SERVICE - Frontend API Client for Category Management
//============================================================================
// Quick Function Reference with Examples:
//
// CATEGORY TYPES:
//   • getAllCategoryTypes({limit: 50}) → {categoryTypes: [...], nextToken, total}
//   • searchCategoryTypes('plato') → filtered category types containing 'plato'
//   • getCategoryTypesByPrefix('plato_') → exact prefix matching (client-side filtered)
//   • getCategoryTypeById(39) → {categoryType: {...}, categoryValues: [...]}
//   • getCategoryTypeByName('account_status') → category type + values by name
//
// CATEGORY VALUES:
//   • getCategoryValues(39, {activeOnly: true}) → all active values for category type ID 39
//   • searchCategoryValues(39, 'act') → search values within category type
//
// CONVENIENCE METHODS:
//   • getActiveValuesByTypeName('user_role') → {categoryType, categoryValues}
//   • getCategoryOptions('account_status') → [{id, key, description}] for dropdowns
//   • hasValue('user_role', 'admin') → true/false if value exists and is active
//   • getMultipleCategoryTypes(['user_role', 'account_status']) → batch fetch
//
// VALIDATION:
//   • getValueKeys('country_code') → {valueKeys: ['ca', 'us', 'mx', ...], count: 3}
//   • getMultipleValueKeys(['country_code', 'user_type']) → batch value keys
//   • validateValue('account_status', 'active') → {isValid: true, validValues: [...]}
//   • validateMultipleValues({user_role: 'admin', status: 'active'}) → validation results
//   • getValidationSchema() → complete schema for common categories
//
// FORM HELPERS:
//   • createFieldValidator('country_code') → returns validation function for forms
//   • validateForm(formData, fieldMappings) → validate entire form against categories
//
// CACHING:
//   • getCachedValueKeys('user_role') → cached value keys (5min TTL)
//   • preloadValidationData(['country_code', 'user_type']) → warm cache
//   • _validationCache.clear() → manually clear cache
//
// DATA STRUCTURE:
//   Category Type: {category_type_id: 39, category_name: 'account_status', description: '...'}
//   Category Value: {category_value_id: 490, value_key: 'active', description: 'Account is active'}
//   Options Format: {id: 490, key: 'active', description: 'Account is active', isActive: true}
//============================================================================

import { apiService } from './api';
import { API_ENDPOINTS } from '../config/constants';

/**
 * Comprehensive Category Service
 * Provides access to all category-related data with proper error handling,
 * caching, and response transformation
 */
export const categoryService = {
  
  // ============================================================================
  // CATEGORY TYPES OPERATIONS (using /master/lookup/get)
  // ============================================================================
  
  /**
   * Get all category types with optional filtering
   * @param {Object} options - Query options
   * @param {number} options.limit - Maximum number of results
   * @param {string} options.nextToken - Pagination token
   * @param {string} options.search - Search term for filtering
   * @returns {Promise<Object>} Response with category_types array and pagination
   */
  getAllCategoryTypes: async (options = {}) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.GET, {
        params: {
          type: 'category_type',
          ...options
        }
      });
      
      return {
        categoryTypes: response.category_types || [],
        nextToken: response.next_token || null,
        total: response.category_types?.length || 0
      };
    } catch (error) {
      console.error('Error getting all category types:', error);
      throw new Error(`Failed to fetch category types: ${error.message}`);
    }
  },

  /**
   * Get category types filtered by search term (uses contains logic)
   * @param {string} searchTerm - Term to search for in category_name or description
   * @param {Object} options - Additional query options
   * @returns {Promise<Object>} Filtered category types
   */
  searchCategoryTypes: async (searchTerm, options = {}) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.GET, {
        params: {
          type: 'category_type',
          search: searchTerm,
          ...options
        }
      });
      
      return {
        categoryTypes: response.category_types || [],
        nextToken: response.next_token || null,
        searchTerm,
        total: response.category_types?.length || 0
      };
    } catch (error) {
      console.error(`Error searching category types with term "${searchTerm}":`, error);
      throw new Error(`Failed to search category types: ${error.message}`);
    }
  },

  /**
   * Get category types that start with a specific prefix
   * Note: This uses the search functionality which does "contains" matching
   * @param {string} prefix - Prefix to filter by (e.g., 'plato_')
   * @param {Object} options - Additional query options
   * @returns {Promise<Object>} Filtered category types
   */
  getCategoryTypesByPrefix: async (prefix, options = {}) => {
    try {
      const response = await categoryService.searchCategoryTypes(prefix, options);
      
      // Client-side filtering for exact prefix matching since backend uses "contains"
      const prefixFiltered = response.categoryTypes.filter(type => 
        type.category_name && type.category_name.startsWith(prefix)
      );
      
      return {
        ...response,
        categoryTypes: prefixFiltered,
        total: prefixFiltered.length,
        note: 'Client-side filtered for exact prefix matching'
      };
    } catch (error) {
      console.error(`Error getting category types with prefix "${prefix}":`, error);
      throw new Error(`Failed to fetch category types by prefix: ${error.message}`);
    }
  },

  /**
   * Get a specific category type by ID with its associated values
   * @param {number|string} categoryTypeId - The category type ID
   * @param {Object} options - Options for associated values
   * @returns {Promise<Object>} Category type with values
   */
  getCategoryTypeById: async (categoryTypeId, options = {}) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.GET, {
        params: {
          type: 'category_type',
          id: categoryTypeId,
          ...options
        }
      });
      
      return {
        categoryType: response.category_type || null,
        categoryValues: response.category_values || [],
        nextToken: response.next_token || null
      };
    } catch (error) {
      if (error.status === 404) {
        return {
          categoryType: null,
          categoryValues: [],
          nextToken: null,
          error: `Category type with ID ${categoryTypeId} not found`
        };
      }
      console.error(`Error getting category type by ID ${categoryTypeId}:`, error);
      throw new Error(`Failed to fetch category type: ${error.message}`);
    }
  },

  /**
   * Get a specific category type by name with its associated values
   * @param {string} categoryName - The category name (snake_case)
   * @param {Object} options - Options for associated values
   * @returns {Promise<Object>} Category type with values
   */
  getCategoryTypeByName: async (categoryName, options = {}) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.GET, {
        params: {
          type: 'category_type',
          name: categoryName,
          ...options
        }
      });
      
      return {
        categoryType: response.category_type || null,
        categoryValues: response.category_values || [],
        nextToken: response.next_token || null
      };
    } catch (error) {
      if (error.status === 404) {
        return {
          categoryType: null,
          categoryValues: [],
          nextToken: null,
          error: `Category type with name "${categoryName}" not found`
        };
      }
      console.error(`Error getting category type by name "${categoryName}":`, error);
      throw new Error(`Failed to fetch category type: ${error.message}`);
    }
  },

  // ============================================================================
  // CATEGORY VALUES OPERATIONS (using /master/lookup/get)
  // ============================================================================

  /**
   * Get category values for a specific category type
   * @param {number|string} categoryTypeId - The category type ID
   * @param {Object} options - Query options
   * @param {boolean} options.activeOnly - Filter for active values only
   * @param {string} options.search - Search term for filtering
   * @param {number} options.limit - Maximum number of results
   * @param {string} options.nextToken - Pagination token
   * @returns {Promise<Object>} Category values with pagination
   */
  getCategoryValues: async (categoryTypeId, options = {}) => {
    try {
      const { activeOnly = true, ...otherOptions } = options;
      
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.GET, {
        params: {
          type: 'category_value',
          category_type_id: categoryTypeId,
          active_only: activeOnly,
          ...otherOptions
        }
      });
      
      return {
        categoryValues: response.category_values || [],
        nextToken: response.next_token || null,
        categoryTypeId: parseInt(categoryTypeId),
        total: response.category_values?.length || 0
      };
    } catch (error) {
      console.error(`Error getting category values for type ID ${categoryTypeId}:`, error);
      throw new Error(`Failed to fetch category values: ${error.message}`);
    }
  },

  /**
   * Search category values within a specific category type
   * @param {number|string} categoryTypeId - The category type ID
   * @param {string} searchTerm - Search term
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Filtered category values
   */
  searchCategoryValues: async (categoryTypeId, searchTerm, options = {}) => {
    try {
      const response = await categoryService.getCategoryValues(categoryTypeId, {
        search: searchTerm,
        ...options
      });
      
      return {
        ...response,
        searchTerm
      };
    } catch (error) {
      console.error(`Error searching category values for type ID ${categoryTypeId}:`, error);
      throw new Error(`Failed to search category values: ${error.message}`);
    }
  },

  // ============================================================================
  // CONVENIENCE METHODS FOR COMMON OPERATIONS (using /master/lookup/get)
  // ============================================================================

  /**
   * Get all active values for a category type by name
   * @param {string} categoryName - Category type name
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Category values
   */
  getActiveValuesByTypeName: async (categoryName, options = {}) => {
    try {
      // First get the category type
      const typeResponse = await categoryService.getCategoryTypeByName(categoryName, { 
        active_only: true,
        ...options 
      });
      
      if (!typeResponse.categoryType) {
        return {
          categoryType: null,
          categoryValues: [],
          error: `Category type "${categoryName}" not found`
        };
      }
      
      return {
        categoryType: typeResponse.categoryType,
        categoryValues: typeResponse.categoryValues,
        nextToken: typeResponse.nextToken
      };
    } catch (error) {
      console.error(`Error getting active values for category "${categoryName}":`, error);
      throw new Error(`Failed to fetch active values: ${error.message}`);
    }
  },

  /**
   * Get a simple key-value mapping for a category type
   * Useful for dropdowns and select components
   * @param {string} categoryName - Category type name
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Array of {id, key, description} objects
   */
  getCategoryOptions: async (categoryName, options = {}) => {
    try {
      const response = await categoryService.getActiveValuesByTypeName(categoryName, options);
      
      if (!response.categoryValues) {
        return [];
      }
      
      return response.categoryValues.map(value => ({
        id: value.category_value_id,
        key: value.value_key,
        description: value.description || value.value_key,
        isActive: value.is_active === 'true'
      }));
    } catch (error) {
      console.error(`Error getting category options for "${categoryName}":`, error);
      throw new Error(`Failed to fetch category options: ${error.message}`);
    }
  },

  /**
   * Check if a specific value exists in a category type
   * @param {string} categoryName - Category type name
   * @param {string} valueKey - Value key to check
   * @returns {Promise<boolean>} True if value exists and is active
   */
  hasValue: async (categoryName, valueKey) => {
    try {
      const options = await categoryService.getCategoryOptions(categoryName);
      return options.some(option => option.key === valueKey && option.isActive);
    } catch (error) {
      console.error(`Error checking if value "${valueKey}" exists in "${categoryName}":`, error);
      return false;
    }
  },

  /**
   * Get multiple category types and their values in a single call
   * @param {string[]} categoryNames - Array of category type names
   * @param {Object} options - Options to apply to all requests
   * @returns {Promise<Object>} Object with category names as keys
   */
  getMultipleCategoryTypes: async (categoryNames, options = {}) => {
    try {
      const promises = categoryNames.map(name => 
        categoryService.getActiveValuesByTypeName(name, options)
          .then(result => ({ name, ...result }))
          .catch(error => ({ name, error: error.message, categoryValues: [] }))
      );
      
      const results = await Promise.all(promises);
      
      // Transform to object with category names as keys
      return results.reduce((acc, result) => {
        const { name, ...data } = result;
        acc[name] = data;
        return acc;
      }, {});
    } catch (error) {
      console.error('Error getting multiple category types:', error);
      throw new Error(`Failed to fetch multiple categories: ${error.message}`);
    }
  },

  // ============================================================================
  // VALIDATION OPERATIONS (using /master/lookup/validate)
  // ============================================================================

  /**
   * Get just the value keys for a category (for validation purposes)
   * Uses: GET /master/lookup/validate?type=value_keys&category_name=...
   * @param {string} categoryName - Category type name
   * @returns {Promise<Object>} Response with value_keys array
   */
  getValueKeys: async (categoryName) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.VALIDATE, {
        params: {
          type: 'value_keys',
          category_name: categoryName
        }
      });
      
      return {
        categoryName: response.category_name,
        valueKeys: response.value_keys || [],
        count: response.count || 0
      };
    } catch (error) {
      if (error.status === 404) {
        return {
          categoryName,
          valueKeys: [],
          count: 0,
          error: `Category "${categoryName}" not found`
        };
      }
      console.error(`Error getting value keys for "${categoryName}":`, error);
      throw new Error(`Failed to fetch value keys: ${error.message}`);
    }
  },

  /**
   * Get value keys for multiple categories in one API call
   * Uses: POST /master/lookup/validate with body: {"type": "multiple_value_keys", "category_names": [...]}
   * @param {string[]} categoryNames - Array of category names
   * @returns {Promise<Object>} Object with category names as keys, value arrays as values
   */
  getMultipleValueKeys: async (categoryNames) => {
    try {
      const response = await apiService.post(API_ENDPOINTS.MASTER.LOOKUP.VALIDATE, {
        type: 'multiple_value_keys',
        category_names: categoryNames
      });
      
      return {
        results: response.results || {},
        errors: response.errors || {},
        stats: response.stats || {
          requested: categoryNames.length,
          successful: 0,
          failed: 0
        }
      };
    } catch (error) {
      console.error('Error getting multiple value keys:', error);
      throw new Error(`Failed to fetch multiple value keys: ${error.message}`);
    }
  },

  /**
   * Validate a single category value
   * Uses: GET /master/lookup/validate?type=validate&category_name=...&value_key=...
   * @param {string} categoryName - Category type name
   * @param {string} valueKey - Value to validate
   * @returns {Promise<Object>} Validation result
   */
  validateValue: async (categoryName, valueKey) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.VALIDATE, {
        params: {
          type: 'validate',
          category_name: categoryName,
          value_key: valueKey
        }
      });
      
      return {
        categoryName: response.category_name,
        valueKey: response.value_key,
        isValid: response.is_valid,
        validValues: response.valid_values || []
      };
    } catch (error) {
      if (error.status === 404) {
        return {
          categoryName,
          valueKey,
          isValid: false,
          validValues: [],
          error: `Category "${categoryName}" not found`
        };
      }
      console.error(`Error validating value "${valueKey}" for "${categoryName}":`, error);
      throw new Error(`Failed to validate value: ${error.message}`);
    }
  },

  /**
   * Validate multiple category values in one API call
   * Uses: POST /master/lookup/validate with body: {"type": "validate_multiple", "values": {...}}
   * @param {Object} values - Object with category names as keys, values to validate as values
   * @returns {Promise<Object>} Validation results for all values
   */
  validateMultipleValues: async (values) => {
    try {
      const response = await apiService.post(API_ENDPOINTS.MASTER.LOOKUP.VALIDATE, {
        type: 'validate_multiple',
        values: values
      });
      
      return {
        results: response.results || {},
        summary: response.summary || {
          all_valid: false,
          valid_count: 0,
          total_count: 0,
          invalid_fields: []
        }
      };
    } catch (error) {
      console.error('Error validating multiple values:', error);
      throw new Error(`Failed to validate multiple values: ${error.message}`);
    }
  },

  /**
   * Get the complete validation schema for common categories
   * Uses: GET /master/lookup/validate?type=validation_schema
   * @returns {Promise<Object>} Schema with all common categories and their valid values
   */
  getValidationSchema: async () => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.VALIDATE, {
        params: {
          type: 'validation_schema'
        }
      });
      
      return {
        schema: response.schema || {},
        metadata: response.metadata || {
          last_updated: null,
          categories: [],
          stats: {
            total_categories: 0,
            total_values: 0
          }
        }
      };
    } catch (error) {
      console.error('Error getting validation schema:', error);
      throw new Error(`Failed to fetch validation schema: ${error.message}`);
    }
  },

  // ============================================================================
  // BACKWARD COMPATIBILITY VALIDATION (using /master/lookup/get)
  // ============================================================================

  /**
   * Get value keys using the legacy /get endpoint (for backward compatibility)
   * Uses: GET /master/lookup/get?type=value_keys&category_name=...
   * @param {string} categoryName - Category type name
   * @returns {Promise<Object>} Response with value_keys array
   */
  getValueKeysLegacy: async (categoryName) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.GET, {
        params: {
          type: 'value_keys',
          category_name: categoryName
        }
      });
      
      return {
        categoryName: response.category_name,
        valueKeys: response.value_keys || [],
        count: response.count || 0
      };
    } catch (error) {
      console.error(`Error getting value keys (legacy) for "${categoryName}":`, error);
      throw new Error(`Failed to fetch value keys: ${error.message}`);
    }
  },

  /**
   * Validate value using the legacy /get endpoint (for backward compatibility)
   * Uses: GET /master/lookup/get?type=validate&category_name=...&value_key=...
   * @param {string} categoryName - Category type name
   * @param {string} valueKey - Value to validate
   * @returns {Promise<Object>} Validation result
   */
  validateValueLegacy: async (categoryName, valueKey) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.LOOKUP.GET, {
        params: {
          type: 'validate',
          category_name: categoryName,
          value_key: valueKey
        }
      });
      
      return {
        categoryName: response.category_name,
        valueKey: response.value_key,
        isValid: response.is_valid,
        validValues: response.valid_values || []
      };
    } catch (error) {
      console.error(`Error validating value (legacy) "${valueKey}" for "${categoryName}":`, error);
      throw new Error(`Failed to validate value: ${error.message}`);
    }
  },

  // ============================================================================
  // FORM VALIDATION HELPERS
  // ============================================================================

  /**
   * Create a validation function for form fields
   * @param {string} categoryName - Category to validate against
   * @returns {Function} Validation function for use with forms
   */
  createFieldValidator: (categoryName) => {
    let validValues = null;
    
    return async (value) => {
      try {
        // Cache valid values to avoid repeated API calls
        if (!validValues) {
          const response = await categoryService.getValueKeys(categoryName);
          validValues = response.valueKeys;
        }
        
        if (!value || value.trim() === '') {
          return { isValid: true, message: '' }; // Allow empty values
        }
        
        const isValid = validValues.includes(value);
        
        return {
          isValid,
          message: isValid ? '' : `Invalid ${categoryName}. Must be one of: ${validValues.join(', ')}`,
          validValues
        };
      } catch (error) {
        console.error(`Error in field validator for ${categoryName}:`, error);
        return {
          isValid: false,
          message: `Validation error: ${error.message}`,
          validValues: []
        };
      }
    };
  },

  /**
   * Validate an entire form object against multiple categories
   * @param {Object} formData - Form data to validate
   * @param {Object} fieldMappings - Map of form fields to category names
   * @returns {Promise<Object>} Complete validation results
   */
  validateForm: async (formData, fieldMappings) => {
    try {
      // Build values object for validation
      const valuesToValidate = {};
      Object.entries(fieldMappings).forEach(([formField, categoryName]) => {
        if (formData[formField] !== undefined && formData[formField] !== null && formData[formField] !== '') {
          valuesToValidate[categoryName] = formData[formField];
        }
      });
      
      if (Object.keys(valuesToValidate).length === 0) {
        return {
          isValid: true,
          errors: {},
          fieldErrors: {},
          summary: 'No fields to validate'
        };
      }
      
      const validation = await categoryService.validateMultipleValues(valuesToValidate);
      
      // Transform results back to form field names
      const fieldErrors = {};
      Object.entries(fieldMappings).forEach(([formField, categoryName]) => {
        if (validation.results[categoryName] && !validation.results[categoryName].is_valid) {
          const validValues = validation.results[categoryName].valid_values || [];
          fieldErrors[formField] = {
            message: `Invalid ${categoryName}. Must be one of: ${validValues.join(', ')}`,
            validValues
          };
        }
      });
      
      return {
        isValid: validation.summary.all_valid,
        errors: validation.results,
        fieldErrors,
        summary: validation.summary
      };
    } catch (error) {
      console.error('Error validating form:', error);
      throw new Error(`Failed to validate form: ${error.message}`);
    }
  },

  // ============================================================================
  // CACHING HELPERS
  // ============================================================================

  /**
   * Cache for validation data to reduce API calls
   */
  _validationCache: {
    valueKeys: new Map(),
    schemas: new Map(),
    ttl: 5 * 60 * 1000, // 5 minutes
    
    get(key) {
      const item = this.valueKeys.get(key) || this.schemas.get(key);
      if (!item) return null;
      
      if (Date.now() - item.timestamp > this.ttl) {
        this.valueKeys.delete(key);
        this.schemas.delete(key);
        return null;
      }
      
      return item.data;
    },
    
    set(key, data, type = 'valueKeys') {
      const cache = type === 'schemas' ? this.schemas : this.valueKeys;
      cache.set(key, {
        data,
        timestamp: Date.now()
      });
    },
    
    clear() {
      this.valueKeys.clear();
      this.schemas.clear();
    }
  },

  /**
   * Get value keys with caching
   * @param {string} categoryName - Category name
   * @returns {Promise<Object>} Cached or fresh value keys
   */
  getCachedValueKeys: async (categoryName) => {
    try {
      // Check cache first
      const cached = categoryService._validationCache.get(categoryName);
      if (cached) {
        return cached;
      }
      
      // Fetch fresh data
      const response = await categoryService.getValueKeys(categoryName);
      
      // Cache the result
      categoryService._validationCache.set(categoryName, response);
      
      return response;
    } catch (error) {
      console.error(`Error getting cached value keys for "${categoryName}":`, error);
      throw error;
    }
  },

  /**
   * Preload commonly used categories into cache
   * @param {string[]} categoryNames - Categories to preload
   * @returns {Promise<void>}
   */
  preloadValidationData: async (categoryNames = [
    'country_code', 'user_type', 'gender', 'marital_status', 
    'occupation', 'address_type', 'primary_language'
  ]) => {
    try {
      console.log('Preloading validation data for categories:', categoryNames);
      
      const promises = categoryNames.map(categoryName =>
        categoryService.getValueKeys(categoryName)
          .then(response => {
            categoryService._validationCache.set(categoryName, response);
            return { categoryName, success: true };
          })
          .catch(error => {
            console.warn(`Failed to preload ${categoryName}:`, error.message);
            return { categoryName, success: false, error: error.message };
          })
      );
      
      const results = await Promise.all(promises);
      
      const successful = results.filter(r => r.success).length;
      console.log(`Preloaded ${successful}/${categoryNames.length} validation categories`);
      
      return results;
    } catch (error) {
      console.error('Error preloading validation data:', error);
      throw error;
    }
  },

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Transform backend response to a consistent format
   * @param {Object} response - Raw API response
   * @returns {Object} Normalized response
   */
  normalizeResponse: (response) => {
    // Handle different response structures from backend
    if (response.category_types) {
      return {
        type: 'category_types',
        items: response.category_types,
        nextToken: response.next_token
      };
    }
    
    if (response.category_values) {
      return {
        type: 'category_values',
        items: response.category_values,
        nextToken: response.next_token
      };
    }
    
    if (response.category_type) {
      return {
        type: 'category_type_with_values',
        categoryType: response.category_type,
        categoryValues: response.category_values || [],
        nextToken: response.next_token
      };
    }
    
    return response;
  },

  /**
   * Build query parameters for API calls
   * @param {Object} options - Options object
   * @returns {Object} Clean parameters object
   */
  buildQueryParams: (options) => {
    const params = {};
    
    // Map frontend options to backend parameter names
    if (options.limit) params.limit = options.limit;
    if (options.nextToken) params.next_token = options.nextToken;
    if (options.search) params.search = options.search;
    if (typeof options.activeOnly === 'boolean') params.active_only = options.activeOnly;
    
    return params;
  }
};

export default categoryService;